import { API_BASE_URL } from '../config';

// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// GET /punte - get all punte
export async function getAllPunte() {
  const response = await fetch(`${API_BASE_URL}/punte`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error("Kon nie punte laai nie");
  }
  
  return await response.json();
}

// GET /punte/:id - get specific punt by ID
export async function getPuntById(id) {
  const response = await fetch(`${API_BASE_URL}/punte/${id}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Punt nie gevind nie");
    }
    throw new Error("Kon nie punt laai nie");
  }
  
  return await response.json();
}

// GET /punte/merkblad/:merkblad_id - get all punte for a specific merkblad
export async function getPunteByMerkbladId(merkbladId) {
  const response = await fetch(`${API_BASE_URL}/punte/merkblad/${merkbladId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error("Kon nie punte vir merkblad laai nie");
  }
  
  return await response.json();
}

// GET /punte/span/:span_id - get all punte for a specific span
export async function getPunteBySpanId(spanId) {
  const response = await fetch(`${API_BASE_URL}/punte/span/${spanId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error("Kon nie punte vir span laai nie");
  }
  
  return await response.json();
}

// GET /punte/span/:span_id/beoordelaar/:beoordelaar - get all punte for a specific span and beoordelaar
export async function getPunteBySpanAndBeoordelaar(spanId, beoordelaar) {
  const response = await fetch(`${API_BASE_URL}/punte/span/${spanId}/beoordelaar/${encodeURIComponent(beoordelaar)}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error("Kon nie punte vir span en beoordelaar laai nie");
  }

  return await response.json();
}

// GET /punte/beoordelaar/:beoordelaar - get all punte a beoordelaar has given (own punte, or any as admin)
export async function getPunteByBeoordelaar(beoordelaar) {
  const response = await fetch(`${API_BASE_URL}/punte/beoordelaar/${encodeURIComponent(beoordelaar)}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error("Kon nie punte vir beoordelaar laai nie");
  }

  return await response.json();
}

// GET /punte/merkblad/:merkblad_id/span/:span_id - get punt for specific merkblad and span combination
export async function getPuntByMerkbladAndSpan(merkbladId, spanId) {
  const response = await fetch(`${API_BASE_URL}/punte/merkblad/${merkbladId}/span/${spanId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Punt vir hierdie merkblad en span kombinasie nie gevind nie");
    }
    throw new Error("Kon nie punt laai nie");
  }
  
  return await response.json();
}

// GET /punte/rondte/:rondte_id - get all punte for all teams in a specific round
export async function getPunteByRondteId(rondteId) {
  const response = await fetch(`${API_BASE_URL}/punte/rondte/${rondteId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error("Kon nie punte vir rondte laai nie");
  }
  
  return await response.json();
}

// POST /punte - create a new punt
export async function createPunt(punteData) {
  const response = await fetch(`${API_BASE_URL}/punte`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(punteData)
  });

  if (!response.ok) {
    throw new Error("Kon nie punt skep nie");
  }

  return await response.json();
}

// PUT /punte/:id - update a punt by ID
export async function updatePunt(id, punteData) {
  const response = await fetch(`${API_BASE_URL}/punte/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(punteData)
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Punt nie gevind nie");
    }
    throw new Error("Kon nie punt opdateer nie");
  }

  return await response.json();
}

// PUT /punte/:id - update multiple punte by IDs
export async function updateMultiplePunte(ids, punteData) {
  const updatePromises = ids.map(id => updatePunt(id, punteData));
  
  try {
    const results = await Promise.all(updatePromises);
    return results;
  } catch (error) {
    throw new Error(`Kon nie alle punte opdateer nie: ${error.message}`);
  }
}


// DELETE /punte/:id - delete punt by ID
export async function deletePunt(id) {
  const response = await fetch(`${API_BASE_URL}/punte/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Punt nie gevind nie");
    }
    throw new Error("Kon nie punt verwyder nie");
  }

  return await response.json();
}

// DELETE /punte/merkblad/:merkblad_id/span/:span_id - delete punt by merkblad and span combination
export async function deletePuntByMerkbladAndSpan(merkbladId, spanId) {
  const response = await fetch(`${API_BASE_URL}/punte/merkblad/${merkbladId}/span/${spanId}`, {
    method: "DELETE",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Punt vir hierdie merkblad en span kombinasie nie gevind nie");
    }
    throw new Error("Kon nie punt verwyder nie");
  }

  return await response.json();
}

// DELETE /punte/merkblad/:merkblad_id - delete all punte for a specific merkblad
export async function deletePunteByMerkblad(merkbladId) {
  const response = await fetch(`${API_BASE_URL}/punte/merkblad/${merkbladId}`, {
    method: "DELETE",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error("Kon nie punte vir merkblad verwyder nie");
  }

  return await response.json();
}

// DELETE /punte/span/:span_id - delete all punte for a specific span
export async function deletePunteBySpan(spanId) {
  const response = await fetch(`${API_BASE_URL}/punte/span/${spanId}`, {
    method: "DELETE",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error("Kon nie punte vir span verwyder nie");
  }

  return await response.json();
}

// POST /punte/bulk - create multiple punte for a team's marksheet submission
export async function createBulkPunte(bulkData) {
  const response = await fetch(`${API_BASE_URL}/punte/bulk`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(bulkData)
  });

  if (!response.ok) {
    // Show the server's reason, e.g. "Die rondte is gesluit"
    let error = "Kon nie punte skep nie";
    try {
      error = (await response.json()).error || error;
    } catch {
      // not JSON - keep the general message
    }
    throw new Error(error);
  }

  return await response.json();
}

// GET /punte/rondte/:rondte_id/status - get round status (is_oop)
export async function getRoundStatus(rondteId) {
  const response = await fetch(`${API_BASE_URL}/punte/rondte/${rondteId}/status`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Rondte nie gevind nie");
    }
    throw new Error("Kon nie rondte status laai nie");
  }
  
  return await response.json();
}

// PUT /punte/rondte/:rondte_id/close - close a round (set is_oop to 0)
export async function closeRound(rondteId) {
  const response = await fetch(`${API_BASE_URL}/punte/rondte/${rondteId}/close`, {
    method: "PUT",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Rondte nie gevind nie");
    }
    throw new Error("Kon nie rondte sluit nie");
  }

  return await response.json();
}

// Legacy function - keeping for backward compatibility
export async function stuurPunte(punteData) {
  return await createPunt(punteData);
}

// PUT /punte/rondte/:rondte_id/open - close a round (set is_oop to 1)
export async function openRound(rondteId) {
  const response = await fetch(`${API_BASE_URL}/punte/rondte/${rondteId}/open`, {
    method: "PUT",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Rondte nie gevind nie");
    }
    throw new Error("Kon nie rondte sluit nie");
  }

  return await response.json();
}

// GET /notas/span/:span_id - get all notas for a specific span, from every beoordelaar (admin only)
export async function getNotasBySpanId(spanId) {
  const response = await fetch(`${API_BASE_URL}/punte/notas/span/${spanId}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw new Error("Kon nie notas vir span laai nie");
  }
  return await response.json();
}

// GET /notas/rondte/:rondte_id - get all notas for every team of a round (admin only)
export async function getNotasByRondteId(rondteId) {
  const response = await fetch(`${API_BASE_URL}/punte/notas/rondte/${rondteId}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw new Error("Kon nie notas vir rondte laai nie");
  }
  return await response.json();
}

// GET /notas/span/:span_id/beoordelaar/:beoordelaar - get all notas for a specific span and beoordelaar
export async function getNotasBySpanAndBeoordelaar(spanId, beoordelaar) {
  const response = await fetch(`${API_BASE_URL}/punte/notas/span/${spanId}/beoordelaar/${encodeURIComponent(beoordelaar)}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw new Error("Kon nie notas vir span en beoordelaar laai nie");
  }
  return await response.json();
}

// POST /notas - create a new nota
export async function createNota(notaData) {
  const response = await fetch(`${API_BASE_URL}/punte/notas`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(notaData)
  });
  if (!response.ok) {
    throw new Error("Kon nie nota skep nie");
  }
  return await response.json();
}

// PUT /notas/:id - update a nota by ID
export async function updateNota(id, notaData) {
  const response = await fetch(`${API_BASE_URL}/punte/notas/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(notaData)
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Nota nie gevind nie");
    }
    throw new Error("Kon nie nota opdateer nie");
  }
  return await response.json();
}
